import { useState } from 'react';
import { useCompany } from '../App';
import { api } from '../lib/api';
import { Button, Card, ErrorBanner, Field, Flash, Input, Modal, Select } from '../components/ui';
import { date, enumLabel } from '../lib/format';

const ENTITY_TYPES = ['C_CORP', 'S_CORP', 'LLC', 'LP', 'PLC', 'OTHER'];

/**
 * Company-level details: legal name, entity type, jurisdiction, tax ID,
 * currency, fiscal year end. PATCH /companies/:id has always accepted these
 * (ADMIN-gated) but nothing in the frontend ever called it — the only way to
 * fix a typo'd legal name or correct the jurisdiction was a direct API call.
 */
export default function Settings() {
  const { company, reload } = useCompany();
  const [editing, setEditing] = useState(false);
  const [flash, setFlash] = useState(null);

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <p>Entity details recorded for this company. Changes here do not touch the cap table.</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setEditing(true)}>Edit</Button>
        </div>
      </div>

      <Flash message={flash} onDone={() => setFlash(null)} />

      <Card>
        <div className="form-grid">
          <Field label="Name"><div>{company.name}</div></Field>
          <Field label="Legal name"><div>{company.legal_name ?? '—'}</div></Field>
          <Field label="Entity type"><div>{company.entity_type ? enumLabel(company.entity_type) : '—'}</div></Field>
          <Field label="Jurisdiction"><div>{company.jurisdiction ?? '—'}</div></Field>
          <Field label="Incorporated"><div>{company.incorporation_date ? date(company.incorporation_date) : '—'}</div></Field>
          <Field label="Tax ID (EIN)"><div>{company.tax_id ?? '—'}</div></Field>
          <Field label="Currency"><div>{company.currency ?? '—'}</div></Field>
          <Field label="Fiscal year end"><div>{company.fiscal_year_end ?? '—'}</div></Field>
        </div>
      </Card>

      {editing ? (
        <EditCompanyModal
          companyId={company.id}
          company={company}
          onClose={() => setEditing(false)}
          onSaved={(msg) => { setEditing(false); setFlash(msg); reload(); }}
        />
      ) : null}
    </div>
  );
}

/**
 * Tax ID is write-only, deliberately never prefilled from the current value:
 * every read of it comes back masked (see maskCompany), and prefilling a
 * masked value would silently overwrite the real one with asterisks the
 * instant this form saved — the same reason Stakeholders' edit modal treats
 * its own tax_id field the same way.
 */
function EditCompanyModal({ companyId, company, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: company.name ?? '',
    legalName: company.legal_name ?? '',
    entityType: company.entity_type ?? '',
    jurisdiction: company.jurisdiction ?? '',
    incorporationDate: company.incorporation_date ? String(company.incorporation_date).slice(0, 10) : '',
    currency: company.currency ?? '',
    fiscalYearEnd: company.fiscal_year_end ?? '',
    taxId: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const fe = error?.fieldErrors ?? {};

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = { name: form.name };
      if (form.legalName) body.legal_name = form.legalName;
      if (form.entityType) body.entity_type = form.entityType;
      if (form.jurisdiction) body.jurisdiction = form.jurisdiction;
      if (form.incorporationDate) body.incorporation_date = form.incorporationDate;
      if (form.currency) body.currency = form.currency;
      if (form.fiscalYearEnd) body.fiscal_year_end = form.fiscalYearEnd;
      if (form.taxId) body.tax_id = form.taxId;
      await api.updateCompany(companyId, body);
      onSaved(`Updated ${form.name}.`);
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Edit company details"
      onClose={onClose}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={saving} onClick={submit}>Save</Button></>}
    >
      <ErrorBanner error={error} />
      <form onSubmit={submit}>
        <div className="form-grid">
          <Field label="Name" required error={fe.name}>
            <Input value={form.name} onChange={set('name')} required autoFocus />
          </Field>
          <Field label="Legal name">
            <Input value={form.legalName} onChange={set('legalName')} />
          </Field>
          <Field label="Entity type">
            <Select value={form.entityType} onChange={set('entityType')}>
              <option value="">—</option>
              {ENTITY_TYPES.map((t) => <option key={t} value={t}>{enumLabel(t)}</option>)}
            </Select>
          </Field>
          <Field label="Jurisdiction" hint="e.g. Delaware">
            <Input value={form.jurisdiction} onChange={set('jurisdiction')} />
          </Field>
          <Field label="Incorporated" error={fe.incorporation_date}>
            <Input type="date" value={form.incorporationDate} onChange={set('incorporationDate')} />
          </Field>
          <Field label="Currency" hint="3-letter code, e.g. USD" error={fe.currency}>
            <Input value={form.currency} onChange={set('currency')} maxLength={3} />
          </Field>
          <Field label="Fiscal year end" hint="e.g. 12-31">
            <Input value={form.fiscalYearEnd} onChange={set('fiscalYearEnd')} />
          </Field>
          <Field
            label="Tax ID (EIN)"
            hint="Write-only — every read of this comes back masked, so it is never shown here. Leave blank to keep the current value."
          >
            <Input value={form.taxId} onChange={set('taxId')} placeholder="Leave blank to keep unchanged" />
          </Field>
        </div>
      </form>
    </Modal>
  );
}
